import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError } from "../../lib/api.js";
import { useRouter } from "../../lib/router.js";
import { PageSpinner } from "../../components/ui.js";
import { IconCheck, IconChevronRight } from "../../components/icons.js";

interface SetRow { id: string; setNumber: number; weightLb: number | null; reps: number | null; isPr: boolean }
interface Ex { id: string; name: string; muscleGroup: string | null; targetSets: number | null; targetReps: string | null; previous: string | null; sets: SetRow[] }
interface Workout { id: string; name: string; status: string; exercises: Ex[] }

export function WorkoutModePage({ id }: { id: string }) {
  const { navigate } = useRouter();
  const [workout, setWorkout] = useState<Workout | null>(null);
  const [idx, setIdx] = useState(0);
  const [weight, setWeight] = useState("");
  const [reps, setReps] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [rest, setRest] = useState(0);
  const restRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await api.get<{ workout: Workout }>(`/api/workouts/${id}`);
      setWorkout(data.workout);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load workout.");
    }
  }, [id]);
  useEffect(() => { void load(); }, [load]);

  useEffect(() => () => { if (restRef.current) clearInterval(restRef.current); }, []);

  function startRest(seconds: number) {
    if (restRef.current) clearInterval(restRef.current);
    setRest(seconds);
    restRef.current = setInterval(() => {
      setRest((r) => {
        if (r <= 1) { if (restRef.current) clearInterval(restRef.current); return 0; }
        return r - 1;
      });
    }, 1000);
  }

  if (!workout) {
    return <div><div className="page-header"><h1>Workout</h1></div>{error ? <div className="alert alert-danger">{error}</div> : <PageSpinner />}</div>;
  }

  const ex = workout.exercises[idx];
  if (!ex) return null;
  const nextSetNumber = ex.sets.length + 1;

  async function completeSet() {
    setError(null);
    const w = weight === "" ? null : Number(weight);
    const r = reps === "" ? null : Number(reps);
    if (r === null) { setError("Enter reps."); return; }
    try {
      await api.post(`/api/workouts/${id}/exercises/${ex!.id}/sets`, { setNumber: nextSetNumber, weightLb: w, reps: r });
      startRest(90);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save set.");
    }
  }

  async function finish() {
    await api.post(`/api/workouts/${id}/complete`).catch(() => undefined);
    navigate("/training");
  }

  return (
    <div className="workout-mode">
      <div className="row-between">
        <button className="btn btn-sm btn-ghost" onClick={() => navigate("/training")}>Exit</button>
        <span className="dim text-sm">Exercise {idx + 1} of {workout.exercises.length}</span>
      </div>

      <div className="card card-pad-lg mt-4 center">
        <h1 style={{ fontSize: "var(--text-2xl)" }}>{ex.name}</h1>
        {ex.muscleGroup && <div className="dim text-sm">{ex.muscleGroup}</div>}
        <div className="row mt-2 wrap" style={{ justifyContent: "center", gap: "var(--space-2)" }}>
          {ex.targetSets && <span className="badge badge-muted">Target {ex.targetSets}×{ex.targetReps ?? "—"}</span>}
          {ex.previous && <span className="badge badge-accent">Last: {ex.previous}</span>}
        </div>
      </div>

      {rest > 0 && (
        <div className="alert alert-info center mt-4" style={{ fontSize: "var(--text-lg)", fontWeight: 600 }}>
          Rest: {Math.floor(rest / 60)}:{String(rest % 60).padStart(2, "0")}
          <button className="btn btn-sm btn-ghost" style={{ marginLeft: "var(--space-3)" }} onClick={() => { if (restRef.current) clearInterval(restRef.current); setRest(0); }}>Skip</button>
        </div>
      )}

      {/* Logged sets */}
      {ex.sets.length > 0 && (
        <div className="card mt-4" style={{ padding: 0, overflow: "hidden" }}>
          {ex.sets.map((s) => (
            <div className="task-row" key={s.id} style={{ alignItems: "center" }}>
              <span className="task-check done"><IconCheck width={16} height={16} /></span>
              <span className="grow">Set {s.setNumber}: <strong>{s.weightLb ?? "—"}{s.weightLb ? " lb" : ""} × {s.reps}</strong></span>
              {s.isPr && <span className="badge badge-success">PR ★</span>}
            </div>
          ))}
        </div>
      )}

      {/* Entry */}
      <div className="card mt-4">
        <div className="nav-section-label" style={{ padding: "0 0 var(--space-2)" }}>Set {nextSetNumber}</div>
        {error && <div className="alert alert-danger">{error}</div>}
        <div className="row" style={{ gap: "var(--space-3)" }}>
          <div className="field grow" style={{ marginBottom: 0 }}>
            <label>Weight (lb)</label>
            <input className="input" type="number" inputMode="decimal" value={weight} onChange={(e) => setWeight(e.target.value)} style={{ fontSize: "var(--text-xl)", height: 56 }} />
          </div>
          <div className="field grow" style={{ marginBottom: 0 }}>
            <label>Reps</label>
            <input className="input" type="number" inputMode="numeric" value={reps} onChange={(e) => setReps(e.target.value)} style={{ fontSize: "var(--text-xl)", height: 56 }} />
          </div>
        </div>
        <button className="btn btn-primary btn-block mt-4" style={{ height: 54, fontSize: "var(--text-md)" }} onClick={completeSet}>
          Complete set {nextSetNumber}
        </button>
      </div>

      {/* Navigation */}
      <div className="row mt-6">
        <button className="btn" disabled={idx === 0} onClick={() => { setIdx((i) => Math.max(0, i - 1)); setWeight(""); setReps(""); }}>Back</button>
        {idx < workout.exercises.length - 1 ? (
          <button className="btn btn-primary grow" onClick={() => { setIdx((i) => i + 1); setWeight(""); setReps(""); }}>
            Next exercise <IconChevronRight width={16} height={16} />
          </button>
        ) : (
          <button className="btn btn-primary grow" onClick={finish}>Finish workout</button>
        )}
      </div>
    </div>
  );
}
